import { useCallback } from 'react'

import { QueryObserverResult } from '@tanstack/react-query'
import { Dictionary } from 'lodash'

import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_ACTIVE_ORG } from '~/constants/refetch-intervals'
import { useActiveOrgId } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'
import { useCookieStore } from '~/store/cookieStore'
import { AuthOrg } from '~/types'
import { captureException, setSentryTag } from '~/utils/errors'

export type ActiveOrgResponse = {
  org: AuthOrg
  featureFlags: Dictionary<boolean>
}

export const getActiveOrgQueryKey = (activeOrgId: string | undefined) => [
  QUERY_KEYS.ACTIVE_ORG,
  activeOrgId,
]

export type useActiveOrgReturn = {
  org: AuthOrg
  isLoading: boolean
  featureFlags: Dictionary<boolean>
  refetch: () => Promise<QueryObserverResult<ActiveOrgResponse, Error>>
  isFetchedAfterMount: boolean
}

export const useActiveOrg = () => {
  const { user: authUser } = useAuthUser()
  const activeOrgId = useActiveOrgId()
  const { serverGetRequest } = useServerRequests()
  const removeActiveOrgId = useCookieStore((s) => s.removeActiveOrgId)

  const fetchActiveOrg = useCallback(async () => {
    if (!activeOrgId) {
      throw new Error('No orgId')
    }

    const activeOrgResponse = (await serverGetRequest<ActiveOrgResponse>({
      path: `/api/org/${activeOrgId}`,
      onError: (error) => {
        captureException(new Error(`useActiveOrg: failed to fetch org`), {
          extra: {
            activeOrgId,
            statusCode: error.response?.status,
            errorMessage: error.message,
          },
        })
        if (error.response?.status === 403) removeActiveOrgId()
        // throwing here means activeOrgResponse can no longer be undefined,
        // hence the ! on the whole call
        throw error
      },
    }))!

    if (activeOrgResponse.data.org.id) {
      setSentryTag('activeOrgId', activeOrgResponse.data.org.id)
    }

    return activeOrgResponse.data
  }, [activeOrgId, serverGetRequest, removeActiveOrgId])

  const {
    data: responseData,
    isLoading: isLoadingOrg,
    isFetched,
    refetch,
    isFetchedAfterMount,
  } = useQueryWrapper<ActiveOrgResponse>({
    queryKey: getActiveOrgQueryKey(activeOrgId),
    queryFn: fetchActiveOrg,
    refetchInterval: REFETCH_ACTIVE_ORG,
    enabled: !!authUser,
    initialData: (): ActiveOrgResponse | undefined => {
      if (authUser && authUser.orgs && activeOrgId) {
        const userOrg = authUser.orgs.find((org) => org.id === activeOrgId)
        if (userOrg) {
          return {
            org: {
              ...userOrg,
              invitations: [],
              members: [],
              passkeys: [],
              smartAccountsChainIds: [],
              requireMemo: false,
            },
            featureFlags: {},
          }
        }
      }
    },
  })

  return {
    org: responseData?.org,
    featureFlags: responseData?.featureFlags,
    // Bootstrap data has no network settings; it is not a confirmed empty list.
    isLoading: isLoadingOrg || (!!activeOrgId && !isFetched),
    refetch,
    isFetchedAfterMount,
  }
}
